import { useMediaQuery, Stack } from '@ltvco/refresh-lib/theme';
import { motion } from 'framer-motion';
import { useState, useEffect, useContext } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import {
  BrandLogoChat,
  ChatPageContainer,
  ChatPageSidebarContainer,
  ChatPageMessagesContainer,
  ChatPageSidebar,
  ChatPageSidebarMobile,
  ChatMenuStack,
  MenuIcon,
  ReportColumn,
  FiltersColumn,
  SourcesColumn,
} from 'components/ChatBV/components';
import { SEARCH_TYPES } from 'components/ChatBV/constants';
import { ChatContext, ChatLayoutContext } from 'components/ChatBV/context';

interface ChatLayoutProps {
  onLogout: () => void;
}

export const ChatLayout = ({ onLogout }: ChatLayoutProps) => {
  const navigate = useNavigate();
  const { reportState } = useContext(ChatLayoutContext);
  const { sendMessage } = useContext(ChatContext);

  const {
    openReport,
    openFilters,
    openSources,
    currentSources,
    closeAllPanels,
    permalink,
    reportData,
    type,
    searchParams,
    setSearchParams,
    collectionType,
    setCollectionType,
    handleCloseReport,
    handleChatTeaserClick,
    handleCloseFilters,
    handleCloseSources,
    handleRelatedReportClick,
  } = reportState;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  // Use MUI's useMediaQuery with custom breakpoint to target exactly 1024px
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');
  const isColumnOpen = openReport || openFilters || openSources;
  const currentWidth = '500px';

  const handleOpenReportInChatBV = (message: string) => {
    sendMessage(message);
  };

  useEffect(() => {
    document.body.classList.add('chat-page');

    return () => {
      document.body.classList.remove('chat-page');
    };
  }, []);

  const headerLogoVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
  };

  const handleMenuClick = () => {
    if (isSidebarOpen) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsSidebarOpen(false);
        setIsAnimatingOut(false);
      }, 300);
    } else {
      setIsSidebarOpen(true);
    }
  };

  const handleRecentReportsClick = () => {
    navigate('/chatbv/chat/library');
    // Close mobile sidebar when opening recent reports
    closeAllPanels();
    if (isSidebarOpen) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsSidebarOpen(false);
        setIsAnimatingOut(false);
      }, 300);
    }
  };

  const handleCloseRecentReports = () => {
    navigate('/chatbv/chat');
    closeAllPanels();
  };

  const handleUpgradePlanClick = () => {
    navigate('/chatbv/upgrade/plan');
  };

  return (
    <ChatPageContainer>
      {!isMobileBreakpoint ? (
        <ChatPageSidebarContainer>
          <ChatPageSidebar
            onRecentReportsClick={handleRecentReportsClick}
            onCloseRecentReports={handleCloseRecentReports}
            onUpgradePlanClick={handleUpgradePlanClick}
            onLogout={onLogout}
          />
        </ChatPageSidebarContainer>
      ) : (
        <>
          <ChatMenuStack>
            <motion.div
              variants={headerLogoVariants}
              initial="hidden"
              animate={'visible'}
            >
              <BrandLogoChat
                width="112px"
                height="18px"
                sx={{
                  transform: 'translateY(0) translateX(0) scale(0.7)',
                }}
              />
            </motion.div>
            <MenuIcon onClick={handleMenuClick} />
          </ChatMenuStack>
          {(isSidebarOpen || isAnimatingOut) && (
            <ChatPageSidebarMobile
              isOpen={isSidebarOpen && !isAnimatingOut}
              setIsOpen={setIsSidebarOpen}
              onRecentReportsClick={handleRecentReportsClick}
              onCloseRecentReports={handleCloseRecentReports}
              onLogout={onLogout}
            />
          )}
        </>
      )}
      <Stack
        direction={isColumnOpen ? 'row' : 'column'}
        width="100%"
        height="100%"
        spacing={0}
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          ...(isColumnOpen &&
            !isMobileBreakpoint && {
              gap: '40px',
            }),
        }}
      >
        <ChatPageMessagesContainer
          sx={{
            width: isColumnOpen && !isMobileBreakpoint ? '60%' : '100%',
            height: isColumnOpen ? '98%' : '100%',
            minWidth: 0,
            ...(isColumnOpen &&
              isMobileBreakpoint && {
                width: 0,
                flexShrink: 1,
                overflow: 'hidden',
              }),
          }}
        >
          <Outlet />
        </ChatPageMessagesContainer>
        {openReport && (
          <ReportColumn
            reportData={reportData}
            onClose={handleCloseReport}
            permalink={permalink}
            currentWidth={isMobileBreakpoint ? '100%' : currentWidth}
            handleOpenReportInChatBV={handleOpenReportInChatBV}
            handleOpenPersonReportInChatBV={handleChatTeaserClick(
              SEARCH_TYPES.PERSON
            )}
            handleRelatedReportClick={handleRelatedReportClick}
            type={type}
          />
        )}
        {openFilters && (
          <FiltersColumn
            currentWidth={isMobileBreakpoint ? '100%' : currentWidth}
            isMobileBreakpoint={isMobileBreakpoint}
            onClose={handleCloseFilters}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            collectionType={collectionType}
            setCollectionType={setCollectionType}
          />
        )}
        {openSources && (
          <SourcesColumn
            sources={currentSources}
            currentWidth={isMobileBreakpoint ? '100%' : currentWidth}
            onClose={handleCloseSources}
          />
        )}
      </Stack>
    </ChatPageContainer>
  );
};
